import type { ApiError } from "~infrastructure/.generated/hey/backoffice-backend";

import { DomainError, DomainErrorCode } from "~domain/global/errors/domain-error.ts";

type BackofficeErrorHandler = (error: ApiError) => DomainError;

const DEFAULT_ERROR_HANDLER: BackofficeErrorHandler = (error) =>
  new DomainError(DomainErrorCode.UNEXPECTED_ERROR, error.message);

const BACKOFFICE_KNOWN_ERROR_FACTORY: Readonly<Record<string, BackofficeErrorHandler>> = Object.freeze({
  // =========================== COMMON ERROR CODE ===========================
  //BAD_REQUEST
  "error.bad-request": (error) => new DomainError(DomainErrorCode.BAD_REQUEST_ERROR, error.message),
  //UNEXPECTED
  "error.unexpected": (error) => new DomainError(DomainErrorCode.UNEXPECTED_ERROR, error.message),
  //FORBIDDEN
  "error.forbidden": (error) => new DomainError(DomainErrorCode.FORBIDDEN_ERROR, error.message),
  // VALIDATION_FAIL
  "5000": (error) => new DomainError(DomainErrorCode.VALIDATION_FAILED_ERROR, error.message),
  // DATABASE_ERROR
  "5001": (error) => new DomainError(DomainErrorCode.UNEXPECTED_ERROR, error.message),
  // CONSTRAINTS_VIOLATION
  "5002": (error) => new DomainError(DomainErrorCode.UNEXPECTED_ERROR, error.message),
  // FIELD_MISSING
  "5003": (error) => new DomainError(DomainErrorCode.FIELD_MISSING_ERROR, error.message),
  // NOT_FOUND_ERROR
  "5004": (error) => new DomainError(DomainErrorCode.NOT_FOUND_ERROR, error.message),
  // RETRYABLE_ERROR
  "5008": (error) => new DomainError(DomainErrorCode.UNEXPECTED_ERROR, error.message),

  // =========================== DEVICE ERROR CODE ===========================
  // DEVICE_NOT_FOUND
  "8000": (error) => new DomainError(DomainErrorCode.DEVICE_NOT_FOUND_ERROR, error.message),
  // DEVICE_ALREADY_ON_SITE
  "8001": (error) => new DomainError(DomainErrorCode.DEVICE_ALREADY_ON_SITE_ERROR, error.message),
  // DEVICE_NOT_LINKED_TO_SITE
  "8005": (error) => new DomainError(DomainErrorCode.DEVICE_NOT_LINKED_TO_SITE_ERROR, error.message),
  // DEVICE_HAS_WRONG_TYPE
  "8006": (error) => new DomainError(DomainErrorCode.DEVICE_HAS_WRONG_TYPE_ERROR, error.message),
  // DEVICE_HAS_NO_HW_LINK
  "8007": (error) => new DomainError(DomainErrorCode.DEVICE_HAS_NO_HW_LINK_ERROR, error.message),
  // VX_UNKNOWN_DEVICE
  "8008": (error) => new DomainError(DomainErrorCode.VX_UNKNOWN_DEVICE_ERROR, error.message),
  // GATEWAY_NOT_LINKED_TO_SITE
  "8009": (error) => new DomainError(DomainErrorCode.GATEWAY_NOT_LINKED_TO_SITE_ERROR, error.message),
  // GATEWAY_UNAVAILABLE
  "8011": (error) => new DomainError(DomainErrorCode.GATEWAY_UNAVAILABLE_ERROR, error.message),
  // DEVICE_HAS_WRONG_MODE
  "8012": (error) => new DomainError(DomainErrorCode.DEVICE_HAS_WRONG_MODE_ERROR, error.message),
  // COMMUNICATION_STATE_NOT_FOUND
  "8013": (error) => new DomainError(DomainErrorCode.COMMUNICATION_STATE_NOT_FOUND_ERROR, error.message),
  // GATEWAY_UPDATE_FAILED
  "8014": (error) => new DomainError(DomainErrorCode.GATEWAY_UPDATE_FAILED_ERROR, error.message),

  // =========================== OPERATION ERROR CODE ===========================
  // MISSING_OPERATOR_COMPANY
  "7000": (error) => new DomainError(DomainErrorCode.MISSING_OPERATOR_COMPANY_ERROR, error.message),
  // OPERATION_NOT_FOUND
  "7001": (error) => new DomainError(DomainErrorCode.OPERATION_NOT_FOUND_ERROR, error.message),
  // SITE_OPERATION_LINK_NOT_FOUND
  "7006": (error) => new DomainError(DomainErrorCode.SITE_OPERATION_LINK_NOT_FOUND_ERROR, error.message),
  // SITE_HAS_MULTIPLE_MISSIONS
  "7007": (error) => new DomainError(DomainErrorCode.SITE_HAS_MULTIPLE_MISSIONS_ERROR, error.message),
  // SITE_IS_ATTACHED_TO_MULTIPLE_OPERATIONS
  "7008": (error) => new DomainError(DomainErrorCode.SITE_IS_ATTACHED_TO_MULTIPLE_OPERATIONS_ERROR, error.message),
  // OPERATION_MISSION_NOT_FOUND
  "7009": (error) => new DomainError(DomainErrorCode.OPERATION_MISSION_NOT_FOUND_ERROR, error.message),
});

export function handleBackofficeError(error: ApiError) {
  const handler = BACKOFFICE_KNOWN_ERROR_FACTORY[error.code] ?? DEFAULT_ERROR_HANDLER;
  return handler(error);
}
